import { DEFAULT_NUMBER_OF_BINS } from "../../utils/Constants";

/**
 * Convert RGB to LCH color space
 * L: Lightness (0-100)
 * C: Chroma (0-150, but typically 0-100)
 * H: Hue (0-360)
 */
const rgbToLch = (
  r: number,
  g: number,
  b: number,
): [number, number, number] => {
  // First convert RGB to XYZ
  r = r / 255;
  g = g / 255;
  b = b / 255;

  // Apply gamma correction
  r = r > 0.04045 ? Math.pow((r + 0.055) / 1.055, 2.4) : r / 12.92;
  g = g > 0.04045 ? Math.pow((g + 0.055) / 1.055, 2.4) : g / 12.92;
  b = b > 0.04045 ? Math.pow((b + 0.055) / 1.055, 2.4) : b / 12.92;

  // Observer. = 2°, Illuminant = D65
  const x = r * 0.4124564 + g * 0.3575761 + b * 0.2125378;
  const y = r * 0.2126729 + g * 0.7151522 + b * 0.072175;
  const z = r * 0.0193339 + g * 0.119192 + b * 0.9503041;

  // Convert XYZ to LAB
  const xn = x / 0.95047;
  const yn = y / 1.0;
  const zn = z / 1.08883;

  const fx = xn > 0.008856 ? Math.pow(xn, 1 / 3) : 7.787 * xn + 16 / 116;
  const fy = yn > 0.008856 ? Math.pow(yn, 1 / 3) : 7.787 * yn + 16 / 116;
  const fz = zn > 0.008856 ? Math.pow(zn, 1 / 3) : 7.787 * zn + 16 / 116;

  const l = 116 * fy - 16;
  const a = 500 * (fx - fy);
  const b_lab = 200 * (fy - fz);

  // Convert LAB to LCH
  const c = Math.sqrt(a * a + b_lab * b_lab);
  let h = Math.atan2(b_lab, a) * (180 / Math.PI);
  if (h < 0) h += 360;

  return [l, c, h];
};

/**
 * Convert LCH to RGB color space
 */
const lchToRgb = (
  l: number,
  c: number,
  h: number,
): [number, number, number] => {
  // Convert LCH to LAB
  const hRad = h * (Math.PI / 180);
  const a = c * Math.cos(hRad);
  const b = c * Math.sin(hRad);

  // Convert LAB to XYZ
  const fy = (l + 16) / 116;
  const fx = a / 500 + fy;
  const fz = fy - b / 200;

  const xn = fx * fx * fx > 0.008856 ? fx * fx * fx : (fx - 16 / 116) / 7.787;
  const yn = fy * fy * fy > 0.008856 ? fy * fy * fy : (fy - 16 / 116) / 7.787;
  const zn = fz * fz * fz > 0.008856 ? fz * fz * fz : (fz - 16 / 116) / 7.787;

  const x = xn * 0.95047;
  const y = yn * 1.0;
  const z = zn * 1.08883;

  // Convert XYZ to RGB
  let r = x * 3.2404542 + y * -1.5371385 + z * -0.4985314;
  let g = x * -0.969266 + y * 1.8760108 + z * 0.041556;
  let b_rgb = x * 0.0556434 + y * -0.2040259 + z * 1.0572252;

  // Apply inverse gamma correction
  r = r > 0.0031308 ? 1.055 * Math.pow(r, 1 / 2.4) - 0.055 : 12.92 * r;
  g = g > 0.0031308 ? 1.055 * Math.pow(g, 1 / 2.4) - 0.055 : 12.92 * g;
  b_rgb =
    b_rgb > 0.0031308
      ? 1.055 * Math.pow(b_rgb, 1 / 2.4) - 0.055
      : 12.92 * b_rgb;

  // Clamp values to 0-255 range
  r = Math.max(0, Math.min(255, Math.round(r * 255)));
  g = Math.max(0, Math.min(255, Math.round(g * 255)));
  b_rgb = Math.max(0, Math.min(255, Math.round(b_rgb * 255)));

  return [r, g, b_rgb];
};

/**
 * Convert hex color to RGB
 */
const hexToRgb = (hex: string): [number, number, number] => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? [
        parseInt(result[1], 16),
        parseInt(result[2], 16),
        parseInt(result[3], 16),
      ]
    : [0, 0, 0];
};

/**
 * Convert RGB to hex color
 */
const rgbToHex = (r: number, g: number, b: number): string => {
  return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
};

/**
 * Generate chart trace colors using LCH color space interpolation
 * Similar to https://www.learnui.design/tools/data-color-picker.html palette mode
 */
export const generateChartTraceColors = ({
  quantity,
  seedColorHex,
  endColorHex,
  setMaxLength = 10,
}: {
  quantity: number;
  seedColorHex: string;
  endColorHex: string;
  setMaxLength?: number;
}) => {
  if (quantity === 1) {
    return [seedColorHex];
  }

  // Convert seed color to LCH
  const [r1, g1, b1] = hexToRgb(seedColorHex);
  const [l1, c1, h1] = rgbToLch(r1, g1, b1);

  // Convert end color to LCH
  const [r2, g2, b2] = hexToRgb(endColorHex);
  const [l2, c2, h2] = rgbToLch(r2, g2, b2);

  const colors: string[] = [];

  // Always use alternating forward/reverse interpolation sets
  // and generate at least 4 colors
  let remaining = Math.max(quantity, 4);
  let setIndex = 0;

  while (remaining > 0) {
    const isReversed = setIndex % 2 === 1;
    const isFullSet = remaining >= setMaxLength;
    const setSize = Math.min(setMaxLength, remaining);

    const setColors: string[] = [];

    if (isFullSet) {
      // For reversed sets, generate extra colors and trim first/last
      const generateCount = isReversed ? setMaxLength + 2 : setMaxLength;

      // Generate colors for this set
      for (let i = 0; i < generateCount; i++) {
        const t = i / (generateCount - 1);

        // Linear interpolation in LCH space
        const l = l1 + (l2 - l1) * t;
        const c = c1 + (c2 - c1) * t;

        // For hue, we need to handle circular interpolation
        let hDiff = h2 - h1;

        // For even sets (0, 2, 4...), take the shorter path
        // For odd sets (1, 3, 5...), take the longer path
        if (isReversed) {
          // Take the longer path around the color wheel
          // First normalize to shortest path
          if (hDiff > 180) hDiff -= 360;
          if (hDiff < -180) hDiff += 360;
          // Then reverse it to go the long way
          if (hDiff > 0) {
            hDiff = hDiff - 360;
          } else {
            hDiff = 360 + hDiff;
          }
        } else {
          // Take the shorter path around the color wheel
          if (hDiff > 180) hDiff -= 360;
          if (hDiff < -180) hDiff += 360;
        }

        let h = h1 + hDiff * t;
        if (h < 0) h += 360;
        if (h >= 360) h -= 360;

        // Convert back to RGB and then to hex
        const [r, g, b] = lchToRgb(l, c, h);
        setColors.push(rgbToHex(r, g, b));
      }

      // For reversed sets, trim first and last
      if (isReversed) {
        setColors.shift(); // Remove first (duplicate seed)
        setColors.pop(); // Remove last (duplicate end)
      }

      // Only take the colors we need from this set
      colors.push(...setColors.slice(0, setSize));
    } else {
      // Partial set - generate with extra colors and trim if reversed
      const needsExtraColors = isReversed;
      const generateCount = needsExtraColors ? setSize + 2 : setSize;

      for (let i = 0; i < generateCount; i++) {
        const t = generateCount === 1 ? 0 : i / (generateCount - 1);

        // Linear interpolation in LCH space
        const l = l1 + (l2 - l1) * t;
        const c = c1 + (c2 - c1) * t;

        // For hue, we need to handle circular interpolation
        let hDiff = h2 - h1;

        // For even sets (0, 2, 4...), take the shorter path
        // For odd sets (1, 3, 5...), take the longer path
        if (isReversed) {
          // Take the longer path around the color wheel
          // First normalize to shortest path
          if (hDiff > 180) hDiff -= 360;
          if (hDiff < -180) hDiff += 360;
          // Then reverse it to go the long way
          if (hDiff > 0) {
            hDiff = hDiff - 360;
          } else {
            hDiff = 360 + hDiff;
          }
        } else {
          // Take the shorter path around the color wheel
          if (hDiff > 180) hDiff -= 360;
          if (hDiff < -180) hDiff += 360;
        }

        let h = h1 + hDiff * t;
        if (h < 0) h += 360;
        if (h >= 360) h -= 360;

        // Convert back to RGB and then to hex
        const [r, g, b] = lchToRgb(l, c, h);
        setColors.push(rgbToHex(r, g, b));
      }

      // For reversed partial sets, trim first and last
      if (needsExtraColors) {
        setColors.shift(); // Remove first
        setColors.pop(); // Remove last
      }

      colors.push(...setColors);
    }

    remaining -= setSize;
    setIndex++;
  }

  return colors;
};

/**
 * Generate chart trace colors by stepping around the full color wheel
 * Creates a rainbow effect starting from the seed color
 */
export const generateChartRainbowColors = ({
  quantity,
  seedColorHex,
}: {
  quantity: number;
  seedColorHex: string;
}) => {
  if (quantity === 1) {
    return [seedColorHex];
  }

  // Convert seed color to LCH
  const [r1, g1, b1] = hexToRgb(seedColorHex);
  const [l1, c1, h1] = rgbToLch(r1, g1, b1);

  const colors: string[] = [];

  // Step around the full 360-degree color wheel
  for (let i = 0; i < quantity; i++) {
    // Calculate hue step for even distribution around the wheel
    const hueStep = 360 / quantity;
    const h = (h1 + i * hueStep) % 360;

    // Keep lightness and chroma from seed color for consistency
    // Add slight variation to prevent colors from being too similar
    const lightnessVariation = Math.sin((i / quantity) * Math.PI * 2) * 10;
    const chromaVariation = Math.cos((i / quantity) * Math.PI * 2) * 5;

    let l = l1 + lightnessVariation;
    let c = c1 + chromaVariation;

    // Ensure values are in valid ranges
    l = Math.max(20, Math.min(85, l));
    c = Math.max(20, Math.min(100, c));

    // Convert back to RGB and then to hex
    const [r, g, b] = lchToRgb(l, c, h);
    colors.push(rgbToHex(r, g, b));
  }

  return colors;
};

export const GLQ_AVG_COLOR = "#9E9E9E";

export const getCustomChartTraceColors = (
  client: string,
  isDarkMode: boolean,
) => {
  let traceColors: string[] | undefined = undefined;
  switch (client) {
    case "GMP":
      traceColors = isDarkMode
        ? [
            "#7A4147",
            "#EF6C00",
            "#FF7043",
            "#EF5350",
            "#B91C1C",
            "#FB923C",
            "#F472B6",
            "#FFA07A",
          ]
        : [
            "#2C040F",
            "#F6AE58",
            "#F97066",
            "#F04438",
            "#7F1D1D",
            "#FB923C",
            "#E85A7A",
            "#B45309",
          ];
      break;
  }

  return traceColors;
};

export type Bin = { start?: number; end?: number; size?: number };

export const getBin = ({
  xName,
  yName,
  xData,
  yData,
}: {
  xName?: string;
  yName?: string;
  xData?: number[];
  yData?: number[];
}): { xbin?: Bin; ybin?: Bin } => {
  const binSizes = [
    { name: "AUS Marbling", bin: { start: 0, end: 10, size: 1 } },
    { name: "IMF %", bin: { start: 0, end: 7, size: 0.5 } },
    { name: "Hotweight Kg", bin: { start: 15, end: 30, size: 2 } },
    { name: "HSCW Kg", bin: { start: 15, end: 30, size: 2 } },
    { name: "Viascan Yield %", bin: { start: 45, end: 65, size: 2.5 } },
    { name: "Viascan Gr", bin: { start: 0, end: 20, size: 2 } },
    { name: "LMY %", bin: { start: 50, end: 70, size: 1 } },
    { name: "GLQ", bin: { start: 1, end: 10, size: 0.2 } },
    { name: "Marbling", bin: { start: 0, end: 9, size: 1 } },
  ];

  const findBin = (name: string | undefined): Bin | undefined => {
    return binSizes.find((b) => b.name === name)?.bin;
  };

  const calculateDefaultBin = (data: number[] | undefined): Bin => {
    if (!data || data.length === 0) return { start: 0, end: 10, size: 1 };

    const min = data.reduce((a, b) => Math.min(a, b), Infinity);
    const max = data.reduce((a, b) => Math.max(a, b), -Infinity);
    const buffer = (max - min) * 0.05; // Add 5% buffer to each side

    // Handle the case where all data points are zero
    if (min === 0 && max === 0) {
      return { start: -1, end: 1, size: 0.25 };
    }

    // Handle the case where there is only one data point
    if (data.length === 1) {
      const value = data[0];
      const range = 1; // Define a range around the single value

      return {
        start: value - range / 2,
        end: value + range / 2,
        size: range / 4,
      };
    }

    const start = Math.floor(min - buffer);
    const end = Math.ceil(max + buffer);
    let size = (end - start) / DEFAULT_NUMBER_OF_BINS;
    if (size >= 1) {
      size = Math.ceil(size);
    }

    return { start, end, size };
  };

  return {
    xbin: findBin(xName) || calculateDefaultBin(xData),
    ybin: findBin(yName) || calculateDefaultBin(yData),
  };
};
